// A deliberately small, dependency-free Markdown renderer for KB articles and
// portal copy. It supports the common subset authors actually use: headings,
// bold/italic/code, links, images, unordered/ordered lists, blockquotes, fenced
// code blocks, horizontal rules, and paragraphs.
//
// Safety: we NEVER use dangerouslySetInnerHTML. Everything is built from React
// elements and plain text nodes, so article content cannot inject markup/scripts.
// Links are constrained to http(s)/mailto and open in a new tab with rel=noopener.

import type { JSX, ReactNode } from "react";

const SAFE_LINK = /^(https?:\/\/|mailto:|\/)/i;

/** Inline formatting: code, bold, italic, links, images. Returns React nodes. */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Order matters: inline code first (so we don't format inside it), then
  // images, links, bold, italic. We do a single regex sweep over alternatives.
  const pattern =
    /(`[^`]+`)|(!\[[^\]]*\]\([^)]+\))|(\[[^\]]+\]\([^)]+\))|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(_[^_]+_)/g;

  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const token = m[0];
    const key = `${keyPrefix}-${i++}`;

    if (token.startsWith("`")) {
      nodes.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith("![")) {
      const inner = /!\[([^\]]*)\]\(([^)]+)\)/.exec(token);
      if (inner && SAFE_LINK.test(inner[2])) {
        nodes.push(<img key={key} src={inner[2]} alt={inner[1]} className="md-img" />);
      } else {
        nodes.push(token);
      }
    } else if (token.startsWith("[")) {
      const inner = /\[([^\]]+)\]\(([^)]+)\)/.exec(token);
      if (inner && SAFE_LINK.test(inner[2])) {
        const external = /^https?:/i.test(inner[2]);
        nodes.push(
          <a
            key={key}
            href={inner[2]}
            {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          >
            {inner[1]}
          </a>,
        );
      } else {
        nodes.push(inner ? inner[1] : token);
      }
    } else if (token.startsWith("**")) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else {
      // *italic* or _italic_
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    }
    last = m.index + token.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/** Block-level renderer: splits source into headings, lists, code, quotes, etc. */
export function Markdown({ source }: { source: string }): JSX.Element {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Blank line — skip.
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Fenced code block.
    if (line.trim().startsWith("```")) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        code.push(lines[i]);
        i++;
      }
      i++; // consume closing fence
      blocks.push(
        <pre key={key++} className="md-pre">
          <code>{code.join("\n")}</code>
        </pre>,
      );
      continue;
    }

    // Horizontal rule.
    if (/^\s*([-*_])\1{2,}\s*$/.test(line)) {
      blocks.push(<hr key={key++} className="md-hr" />);
      i++;
      continue;
    }

    // Heading.
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const content = renderInline(heading[2], `h${key}`);
      const Tag = `h${Math.min(level + 1, 6)}` as keyof JSX.IntrinsicElements;
      blocks.push(
        <Tag key={key++} className="md-h">
          {content}
        </Tag>,
      );
      i++;
      continue;
    }

    // Blockquote.
    if (/^\s*>\s?/.test(line)) {
      const quote: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        quote.push(lines[i].replace(/^\s*>\s?/, ""));
        i++;
      }
      blocks.push(
        <blockquote key={key++} className="md-quote">
          {renderInline(quote.join(" "), `q${key}`)}
        </blockquote>,
      );
      continue;
    }

    // Unordered list.
    if (/^\s*[-*+]\s+/.test(line)) {
      const items: ReactNode[] = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        const item = lines[i].replace(/^\s*[-*+]\s+/, "");
        items.push(<li key={items.length}>{renderInline(item, `ul${key}-${items.length}`)}</li>);
        i++;
      }
      blocks.push(
        <ul key={key++} className="md-list">
          {items}
        </ul>,
      );
      continue;
    }

    // Ordered list.
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: ReactNode[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        const item = lines[i].replace(/^\s*\d+\.\s+/, "");
        items.push(<li key={items.length}>{renderInline(item, `ol${key}-${items.length}`)}</li>);
        i++;
      }
      blocks.push(
        <ol key={key++} className="md-list">
          {items}
        </ol>,
      );
      continue;
    }

    // Paragraph — gather consecutive non-blank, non-special lines.
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !lines[i].trim().startsWith("```") &&
      !/^(#{1,6})\s+/.test(lines[i]) &&
      !/^\s*>\s?/.test(lines[i]) &&
      !/^\s*[-*+]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !/^\s*([-*_])\1{2,}\s*$/.test(lines[i])
    ) {
      para.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={key++} className="md-p">
        {renderInline(para.join(" "), `p${key}`)}
      </p>,
    );
  }

  return <div className="markdown">{blocks}</div>;
}
